import React, { memo, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { REPORT_STATUS_STYLES, REPORT_REASON_STYLES } from '@/constants/residents/corkBoardConstants';
import type { CorkBoardReport } from '@/types/residents/corkBoard';

interface ReportReviewListItemProps {
  report: CorkBoardReport;
  isMutating: boolean;
  onView: (report: CorkBoardReport) => void;
  onRemove: (report: CorkBoardReport) => void;
  onDismiss: (report: CorkBoardReport) => void;
  onWarn: (report: CorkBoardReport) => void;
}

export const ReportReviewListItem: React.FC<ReportReviewListItemProps> = memo(
  ({ report, isMutating, onView, onRemove, onDismiss, onWarn }) => {
    const isActionable = report.status === 'pending' || report.status === 'reviewed';

    const formattedDate = useMemo(() => {
      const date = new Date(report.createdAt);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }, [report.createdAt]);

    const statusStyle = REPORT_STATUS_STYLES[report.status] ?? REPORT_STATUS_STYLES.pending;
    const reasonStyle = REPORT_REASON_STYLES[report.reason] ?? REPORT_REASON_STYLES.other;

    return (
      <View style={styles.container}>
        {/* Top row: content type + preview + status */}
        <View style={styles.topRow}>
          <View style={[styles.typeBadge, report.contentType === 'thread' ? styles.threadBadge : styles.replyBadge]}>
            <Text style={styles.typeBadgeText}>
              {report.contentType === 'thread' ? 'Thread' : 'Reply'}
            </Text>
          </View>
          <Text style={styles.contentPreview} numberOfLines={1}>
            {report.contentPreview || '(no preview)'}
          </Text>
          <View style={[styles.statusBadge, { backgroundColor: statusStyle.bg }]}>
            <Text style={[styles.statusBadgeText, { color: statusStyle.text }]}>
              {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
            </Text>
          </View>
        </View>

        {/* Middle row: reporter, reason, date */}
        <View style={styles.middleRow}>
          <Text style={styles.metaText}>Reported by {report.reporter.name}</Text>
          <Text style={styles.metaSeparator}>·</Text>
          <View style={[styles.reasonBadge, { backgroundColor: reasonStyle.bg }]}>
            <Text style={[styles.reasonBadgeText, { color: reasonStyle.text }]}>
              {report.reason.charAt(0).toUpperCase() + report.reason.slice(1)}
            </Text>
          </View>
          <Text style={styles.metaSeparator}>·</Text>
          <Text style={styles.metaText}>{formattedDate}</Text>
        </View>

        {/* Bottom row: actions */}
        <View style={styles.bottomRow}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => onView(report)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={styles.actionText}>View</Text>
          </TouchableOpacity>

          {isActionable && (
            <>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => onRemove(report)}
                disabled={isMutating}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Text style={[styles.deleteText, isMutating && styles.actionTextDisabled]}>
                  Remove
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => onDismiss(report)}
                disabled={isMutating}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Text style={[styles.actionText, isMutating && styles.actionTextDisabled]}>
                  Dismiss
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => onWarn(report)}
                disabled={isMutating}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Text style={[styles.warnText, isMutating && styles.actionTextDisabled]}>
                  Warn
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    );
  },
);

ReportReviewListItem.displayName = 'ReportReviewListItem';

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    gap: 8,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  typeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    flexShrink: 0,
  },
  threadBadge: {
    backgroundColor: '#3B82F6',
  },
  replyBadge: {
    backgroundColor: '#8B5CF6',
  },
  typeBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  contentPreview: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    flexShrink: 0,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: '600',
  },
  middleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  metaText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  metaSeparator: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  reasonBadge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 4,
  },
  reasonBadgeText: {
    fontSize: 11,
    fontWeight: '500',
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.PRIMARY,
  },
  actionTextDisabled: {
    opacity: 0.4,
  },
  deleteText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.ERROR,
  },
  warnText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#D97706',
  },
});
